
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Expense } from '@/types/expense';
import { useAuth } from '@/contexts/auth';
import LoadingSpinner from '@/components/itinerary/LoadingSpinner';
import PaymentInfoCard from './PaymentInfoCard';
import BalanceCard from './BalanceCard';
import SettlementCard from './SettlementCard';
import { useSettlements } from './hooks/useSettlements';

interface SettleUpDashboardProps {
  tripId: string;
  expenses: Expense[];
}

const SettleUpDashboard: React.FC<SettleUpDashboardProps> = ({ tripId, expenses }) => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(true);
  
  const { balances, settlements, paymentInfos, formatCurrency } = useSettlements(expenses, profiles);

  // Fetch user profiles for the trip
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setIsLoading(true);
        
        // Get all user IDs from expenses
        const userIds = new Set<string>();
        expenses.forEach(expense => {
          userIds.add(expense.paid_by);
        });
        
        if (userIds.size === 0) {
          setIsLoading(false);
          return;
        }
        
        // Fetch profiles
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username, avatar_url')
          .in('id', Array.from(userIds));
          
        if (error) throw error;
        
        // Convert to a map for easy lookup
        const profileMap = (data || []).reduce((acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        }, {} as Record<string, any>);
        
        setProfiles(profileMap);
      } catch (error) {
        console.error('Error fetching profiles:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfiles();
  }, [expenses]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (expenses.length === 0) {
    return (
      <div className="border border-gray-200 p-6 text-center text-gray-500 rounded-md">
        No expenses added yet. Add some expenses to see the settlement plan.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {user && <PaymentInfoCard userId={user.id} />}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <BalanceCard 
          balances={balances} 
          formatCurrency={formatCurrency} 
        />
        
        <SettlementCard 
          settlements={settlements} 
          formatCurrency={formatCurrency} 
          paymentInfos={paymentInfos} 
        />
      </div>
    </div>
  );
};

export default SettleUpDashboard;
